import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Utensils } from 'lucide-react';

const CONTINENT_DATA = {
  africa: {
    name: 'Afrique',
    color: 'bg-continent-africa',
    image: 'https://images.unsplash.com/photo-1700625914294-d3246e005957?w=600&q=80',
    description: 'Saveurs épicées et traditions millénaires',
  },
  asia: {
    name: 'Asie',
    color: 'bg-continent-asia',
    image: 'https://images.unsplash.com/photo-1711633648854-50a30a6df74d?w=600&q=80',
    description: 'De la street food aux plats impériaux',
  },
  europe: {
    name: 'Europe',
    color: 'bg-continent-europe',
    image: 'https://images.unsplash.com/photo-1642346854073-a88b5a77038c?w=600&q=80',
    description: 'Gastronomie raffinée et terroirs authentiques',
  },
  north_america: {
    name: 'Amérique du Nord',
    color: 'bg-continent-north-america',
    image: 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=600&q=80',
    description: 'Du comfort food aux saveurs caribéennes',
  },
  south_america: {
    name: 'Amérique du Sud',
    color: 'bg-continent-south-america',
    image: 'https://images.unsplash.com/photo-1650539062879-1ec3b24f1bde?w=600&q=80',
    description: 'Couleurs vibrantes et ingrédients uniques',
  },
  oceania: {
    name: 'Océanie',
    color: 'bg-continent-oceania',
    image: 'https://images.unsplash.com/photo-1571167530149-c1105da4c2c7?w=600&q=80',
    description: 'Fusion des îles du Pacifique',
  },
};

export const ContinentCard = ({ continent, index = 0 }) => {
  const data = CONTINENT_DATA[continent.id] || {
    name: continent.name,
    color: 'bg-worldplate-primary',
    image: 'https://images.unsplash.com/photo-1699572583203-97170ae281c0?w=600&q=80',
    description: 'Découvrez les saveurs du monde',
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
    >
      <Link
        to={`/continents/${continent.id}`}
        className="block group continent-card"
        data-testid={`continent-card-${continent.id}`}
      >
        <div className="relative overflow-hidden rounded-3xl aspect-[4/3] cursor-pointer">
          {/* Background Image */}
          <img
            src={data.image}
            alt={data.name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            loading="lazy"
          />
          
          {/* Color Overlay */}
          <div className={`absolute inset-0 ${data.color} opacity-40 group-hover:opacity-60 continent-card-overlay transition-opacity duration-300`} />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Content */}
          <div className="absolute inset-0 p-6 flex flex-col justify-end">
            <div className="transform group-hover:-translate-y-2 transition-transform duration-300">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                {data.name}
              </h3>
              <p className="text-white/80 text-sm md:text-base mb-3 line-clamp-2">
                {data.description}
              </p>
              
              {/* Stats */}
              <div className="flex items-center gap-4 text-white/90 text-sm">
                <span className="flex items-center gap-1">
                  <Utensils className="w-4 h-4" />
                  {continent.recipe_count} recettes
                </span>
                <span>
                  {continent.country_count} pays
                </span>
              </div>
            </div>

            {/* Arrow */}
            <div className="absolute right-4 bottom-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white group-hover:text-worldplate-deep transition-colors duration-300">
              <ChevronRight className="w-5 h-5 text-white group-hover:text-worldplate-deep" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ContinentCard;
